/**
 * Chat storage — defaults by role (#758).
 *
 * ── A table with ONE save, not a row of buttons each writing on click ───
 *
 * An admin setting defaults is making one decision about the whole
 * organisation, and the two things they need before committing it are what
 * the change does to the PROMISED total and whether it pushes anybody over
 * their limit. Neither can be shown by a control that has already written.
 * Save is inert until something actually differs, so the button is never a
 * no-op that still writes an audit row.
 *
 * ── The note beside each role is DERIVED, never read off its name ───────
 *
 * Whether a role may use the agent at all is a SEPARATE setting (the AI
 * budget). The moment an admin lifts guest's budget, "can't use AI" has to
 * stop being true for guest, and the opposite warning has to take its
 * place: a 0 MB storage limit refuses the agent's OWN screenshots, which
 * breaks the one thing that role can do. Hardcoding either by role name
 * would make the dialog lie as soon as the other page changed.
 *
 * ── `null` and absent still mean opposite things ────────────────────────
 *
 * Clearing a field back to the shipped default removes the override;
 * leaving a number pins it. Unlimited is not offered here — nothing in the
 * UI creates it — but a role that already carries it is shown as such and
 * left alone unless the admin types over it.
 *
 * ⚠️ AND THE ROUTE REFUSES IT NOW TOO (owner, 2026-10-30). This dialog not
 * offering unlimited used to be the only thing standing between an
 * organisation and an unlimited role: `PATCH /:id/roles/chat-storage`
 * accepted `limitBytes: null` for anyone who sent it by hand. It no longer
 * does, so a role cannot BECOME unlimited by any route — which is why this
 * dialog keeps DISPLAYING one (old data) while still offering no way to
 * create one. See the refusal in `features/orgs/api/routes.ts` for why
 * unlimited is unwanted: it is a promise the drive cannot keep, made to a
 * whole role at once, and it silently makes the over-allocation figure
 * understate the promise.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { storageApi, type ChatStorageRoleDefault, type MemberChatStorageRow } from '../../client/storage'
import { formatBytes } from '../budget/bits'
import { roleLabel } from './shared'
import { pinnedBatch } from '@papan/api-client'

const MB = 1024 * 1024
/** The order the roles are presented in, most privileged first. */
const ROLE_ORDER = ['owner', 'admin', 'member', 'viewer', 'guest', 'noaccess']

/** A field is committable only as a whole, non-negative MB count.
 *
 *  Decimal digits ONLY, and bounded — see the twin in
 *  `AdjustChatLimitDialog` for why `Number()` alone was not enough
 *  (full review 3C, finding 8). */
const MAX_TYPED_MB = 16 * 1024 * 1024
function wholeMb(v: string): number | null {
  const t = v.trim()
  if (t === '' || !/^\d+$/.test(t)) return null
  const n = Number(t)
  return Number.isSafeInteger(n) && n >= 0 && n <= MAX_TYPED_MB ? n : null
}

export function RoleChatDefaultsDialog({
  tenantId, roleDefaults, members, quotaBytes, onClose, onSaved,
}: {
  tenantId: string
  roleDefaults: ChatStorageRoleDefault[]
  /** Every member, for "who it changes" and the over-limit count. */
  members: MemberChatStorageRow[]
  quotaBytes?: number
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const sorted = useMemo(
    () => [...roleDefaults].sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)),
    [roleDefaults],
  )
  /** What each role resolves to today, in MB, as the fields start. */
  const initial = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of sorted) {
      const eff = r.isOverridden ? r.overrideBytes ?? null : r.shippedBytes
      m[r.role] = eff === null ? '' : String(Math.round(eff / MB))
    }
    return m
  }, [sorted])
  const [vals, setVals] = useState<Record<string, string>>(initial)

  // `role in vals` guards a role that appeared in `roleDefaults` AFTER the
  // dialog opened. `vals` is seeded once, so such a role has no entry, and
  // without this it reads as "the admin cleared this field".
  //
  // DEFENSIVE, not a live bug: this dialog has one mount site, and the
  // only things that replace `org` are clicks behind its own modal
  // backdrop, so the state is not producible through the app today (round
  // 13, finding 4 — earlier notes here claimed a user-visible warning and
  // overstated it). Kept because `vals` and `initial` can diverge by
  // construction and the failure is silent when they do.
  //
  // TWO mechanisms hold this, not one — an earlier note here claimed
  // "the same fallback is applied to every read of `vals` below", which is
  // false (round 14, finding 5):
  //
  //  - reads that can see ANY role — the field, `isReset`, `typed` — take
  //    the `?? initial[role]` fallback;
  //  - reads that only ever see a CHANGED role — `allValid`, the save loop
  //    — are safe because `touched` gates `changed` on `role in vals`, so
  //    those roles are seeded by definition.
  //
  // Deleting a `role in vals` gate on the belief that the fallback covers
  // everything would break the second group, silently.
  const touched = (role: string) => role in vals && vals[role] !== initial[role]
  const changed = sorted.filter((r) => touched(r.role))
  // FULL REVIEW 2, FINDING 5 — an EMPTY field is the reset, not an error.
  //
  // The docblock above has always said "clearing a field back to the shipped
  // default removes the override", the route has always supported it
  // (omitting `limitBytes` resets) and the client API has the optional
  // argument for it. The dialog did not: `wholeMb('')` is null, so clearing
  // a field made `allValid` false and greyed Save for the WHOLE dialog. The
  // only way out was to retype the shipped number — which PINS it, so the
  // next change to the shipped default silently skips that role. That is
  // exactly the trap the member layer is built to avoid, reproduced one
  // layer up. (`chatStorageShipped` had been written into all four locales
  // for this control and never used — it was specified and not wired. It
  // is the field's placeholder now; a sibling `chatStorageReset`, for a
  // button this does not have, was deleted.)
  // A reset is the user CLEARING a field that had a value — not merely an
  // empty field.
  //
  // An override TO UNLIMITED renders as '' too (there is no number for
  // "unlimited"), so reading every '' as a reset made an untouched
  // unlimited role show the "Default" placeholder, a phantom allocation
  // change in the preview, and a false over-limit warning on a dialog
  // nobody had edited. Comparing against `initial` tells the two apart:
  // unlimited starts empty and stays empty, so it is not a change at all.
  // (Scoped review of full review 2, finding 3.)
  //
  // `?? ''` because `limitAfter` below calls this for every MEMBER, and a
  // membership whose role has no `roleDefaults` row has no entry here — the
  // same guard the line below it already keeps (finding 7).
  const isReset = (role: string) =>
    // `role in vals` here too, not only in `touched`. A role that appeared
    // in `roleDefaults` AFTER the dialog opened has no entry, and without
    // this it reads as an empty field: the preview then drops that role to
    // its shipped number, firing a false over-limit warning and a phantom
    // allocation change on a dialog nobody has edited (round 12, finding
    // 2 — probed, not reasoned). Save stays correctly disabled either way,
    // so this is the DISPLAY half of the same guard.
    role in vals
    && (vals[role] ?? '').trim() === '' && (initial[role] ?? '').trim() !== ''
  // A reset that will be SKIPPED is not a change — otherwise Save lights up
  // for a click that writes nothing and then reports success, against this
  // module's own "never a no-op" promise (round 11, finding 9).
  const writable = sorted.filter((r) => touched(r.role) && !(isReset(r.role) && !r.isOverridden))
  const allValid = changed.every((r) => isReset(r.role) || wholeMb(vals[r.role] ?? '') !== null)

  /** A member's limit AFTER this edit: their own if they have one, else
   *  the role value currently in the field. */
  const limitAfter = (m: MemberChatStorageRow): number | null => {
    if (m.hasOwnLimit) return m.limitBytes
    // A cleared field means "back to what ships", so the preview has to show
    // the SHIPPED number, not the current one — otherwise the allocation
    // figures below would claim a reset changes nothing.
    if (isReset(m.role)) {
      return sorted.find((r) => r.role === m.role)?.shippedBytes ?? m.limitBytes
    }
    const mb = wholeMb(vals[m.role] ?? '')
    return mb === null ? m.limitBytes : mb * MB
  }

  const promisedNow = members.reduce((s, m) => s + (m.limitBytes ?? 0), 0)
  const promisedAfter = members.reduce((s, m) => s + (limitAfter(m) ?? 0), 0)
  // "Cannot upload", not "over" — the gate refuses at `total + net > lim`,
  // so a member sitting EXACTLY on the new limit is blocked, and a limit
  // of 0 blocks whatever they hold. Counting only `>` under-counted both
  // and the copy promised something narrower than the effect (full review
  // 4C, finding 2). The string says what it means now.
  //
  // ONLY THE PEOPLE THIS DIALOG WOULD MOVE, and only once something is
  // edited. Both halves were wrong (full review 43C, B5):
  //
  //  - `limitAfter` returns a member's OWN limit when they have one, so
  //    this counted members with a custom limit — whom no role default can
  //    touch. The warning blamed the admin's pending edit for a state it
  //    cannot cause or cure.
  //  - it was computed unconditionally, so opening the dialog to LOOK
  //    raised a red "1 member cannot upload under the new limits" with
  //    Save disabled and nothing typed. There were no new limits. And
  //    `roleDefaultsCalm`, the quiet state it replaces, promises "you'll
  //    be told here before you save" — which frames the panel as being
  //    about the pending change.
  //
  // So: no own limit (the dialog can move them), and their role actually
  // touched (there IS a new limit for them).
  //
  // `touched` alone covers "nothing edited yet" — with no field typed it is
  // false for every role, so the count is 0 and the calm line shows. A
  // `changed.length === 0 ? 0 :` guard went in alongside it and was pure
  // duplication: no mutation could tell the two apart, which is how it was
  // caught. One way to be right, not two.
  const overAfter = members.filter((m) => {
    if (m.hasOwnLimit || !touched(m.role)) return false
    const lim = limitAfter(m)
    return lim !== null && (lim === 0 || m.totalBytes >= lim)
  }).length

  const save = async () => {
    setSaving(true); setFailed(false)
    // #1316 review round 2 (owner, 2026-10-28): the Save the admin pressed FINISHES as the identity that pressed it —
    // every role's write goes (and is audited) as it; nothing is shown once that session ended
    const batch = pinnedBatch()
    try {
      // Sequential, not parallel: each write audits, and a failure part way
      // leaves the roles before it applied — which is recoverable and
      // visible — rather than an indeterminate mix.
      // `writable`, so the skip is decided in ONE place — the same place
      // Save's enabled state is decided.
      for (const r of writable) {
        // `undefined` is the RESET — a different write from `null`
        // (unlimited) and from a number (pin). See the client's note.
        await batch.run(() => storageApi.setRoleChatStorageLimit(
          tenantId,
          r.role,
          isReset(r.role) ? undefined : (wholeMb(vals[r.role]) ?? 0) * MB,
        ))
      }
      if (batch.ended()) return
      onSaved()
      onClose()
    } catch {
      if (batch.ended()) return
      setFailed(true); setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className="relative z-10 max-w-2xl w-full max-h-[85vh] flex flex-col ws-glass-modal ws-anim-modal-in">

        <div className="px-6 pt-5 pb-3.5 themed-border border-b">
          <h3 className="text-[17px] font-semibold themed-text">{t('settings.storage.roleDefaultsTitle')}</h3>
          <p className="text-xs themed-text-muted mt-1 leading-relaxed">{t('settings.storage.roleDefaultsIntro')}</p>
        </div>

        <div className="overflow-y-auto min-h-0 flex-1">
          <div className="flex px-6 pt-3.5 pb-1 text-[10.5px] tracking-wide themed-text-muted">
            <div className="w-[108px]">{t('settings.storage.roleDefaultsColRole')}</div>
            <div className="w-[118px]">{t('settings.storage.roleDefaultsColDefault')}</div>
            <div className="w-[168px]">{t('settings.storage.roleDefaultsColAffects')}</div>
            <div className="grow" />
          </div>

          {sorted.map((r) => {
            const inRole = members.filter((m) => m.role === r.role)
            const customs = inRole.filter((m) => m.hasOwnLimit).length
            const movable = inRole.length - customs
            // Same `initial` fallback the field and `isReset` use. Left on the bare
  // `vals` read, this made the two disagree: an unseeded role rendered its
  // override (say `0`) in the box while the "0 MB starves this role" note
  // beside it stayed silent, because `typed` saw an empty string (round
  // 13, finding 3). Display and warning read the same value now.
  const typed = wholeMb(vals[r.role] ?? initial[r.role] ?? '')
            // Derived from the AI budget, both ways — see the module doc.
            const starved = !r.aiBlocked && typed === 0 && inRole.length > 0
            const note = r.aiBlocked
              ? t('settings.storage.roleDefaultsNoAi')
              : starved ? t('settings.storage.roleDefaultsStarved') : ''
            return (
              <div key={r.role} className="flex items-center px-6 py-2 themed-border border-t">
                {/* `roleLabel`, not the raw id — this row used to read
                    "Noaccess", and `capitalize` is what made that look
                    deliberate (full review 43C, B9).
                    AND THE CLASS HAD TO GO WITH IT. I kept it on the claim
                    that it was "a no-op, the labels are already
                    capitalised" — false for the two multi-word ones:
                    `text-transform: capitalize` uppercases EVERY word, so
                    the row read "Drive Admin" and "No Access" while the
                    aria-label (unstyled) said "Drive admin". The product's
                    own name is "Drive admin", and the budget screen this
                    one is modelled on renders the label with no such class
                    — which is what "the two now agree" was supposed to
                    mean (scoped review of full review 43, finding 4). */}
                <div className={`w-[108px] text-[13px] font-semibold ${r.aiBlocked ? 'themed-text-muted' : 'themed-text'}`}>
                  {roleLabel(r.role)}
                </div>
                <div className="w-[118px] flex items-center gap-1.5">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    // Falls back to `initial`, not to empty: an unseeded role
                    // is one that arrived after the dialog opened, and
                    // showing its override as a blank box says "shipped
                    // default" about a value that is nothing of the kind.
                    value={vals[r.role] ?? initial[r.role] ?? ''}
                    onChange={(e) => setVals((v) => ({ ...v, [r.role]: e.target.value }))}
                    aria-label={`${roleLabel(r.role)} default`}
                    // What an EMPTY field means — and it means two different
                    // things, so the placeholder has to say which.
                    //
                    // There is no number for "unlimited", so an override TO
                    // unlimited renders empty exactly as a never-overridden
                    // role does. Showing "Default" for both told an admin
                    // their unlimited role was on the shipped default
                    // (round 11, finding 4). Now the placeholder names the
                    // state the field is actually in.
                    placeholder={r.isOverridden && r.overrideBytes === null
                      ? t('settings.storage.chatStorageUnlimited')
                      : t('settings.storage.chatStorageShipped')}
                    className="w-[66px] text-[13px] px-2 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                  />
                  <span className="text-[11.5px] themed-text-muted">MB</span>
                </div>
                <div className="w-[168px] text-[11.5px] themed-text-muted">
                  {inRole.length === 0
                    ? t('settings.storage.roleDefaultsNobody')
                    : `${t('settings.storage.roleDefaultsAffects', { movable, total: inRole.length })}${
                        customs > 0 ? ` · ${t('settings.storage.roleDefaultsCustomSuffix', { count: customs })}` : ''
                      }`}
                </div>
                <div className={`grow text-[11px] ${starved ? 'text-rose-500 font-semibold' : 'themed-text-muted'}`}>{note}</div>
              </div>
            )
          })}

          <div className="px-6 py-3.5 mt-1 themed-border border-t space-y-3">
            <div className="flex items-center gap-2.5 rounded-xl themed-border border themed-bg-secondary px-4 py-3">
              <span className="grow text-[13px] themed-text">{t('settings.storage.roleDefaultsPromised')}</span>
              {promisedAfter !== promisedNow && (
                <span className="text-xs themed-text-muted line-through">{formatBytes(promisedNow)}</span>
              )}
              <span className="text-[15px] font-semibold themed-text">{formatBytes(promisedAfter)}</span>
              {/* `> 0`, not `typeof === 'number'`: a plan can carry a
                  quota of 0, and this page treats that as a real state
                  elsewhere. Dividing by it printed "3.00 GB of 0 B ·
                  Infinity%", or "0 B of 0 B · NaN%" with no members —
                  which reads as a broken screen and tells the admin
                  nothing about whether their edit over-allocates (full
                  review 4C, finding 1). The pool itself is still worth
                  showing; only the percentage is undefined. */}
              {typeof quotaBytes === 'number' && (
                <span className="text-xs themed-text-muted">
                  {t('settings.storage.chatStorageOfPool', { pool: formatBytes(quotaBytes) })}
                  {quotaBytes > 0 && `${' · '}${Math.round((promisedAfter / quotaBytes) * 100)}%`}
                </span>
              )}
            </div>

            {overAfter > 0 ? (
              <div className="flex gap-2.5 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="shrink-0 mt-0.5">
                  <path d="M12 3l9.5 16.5h-19z" /><path d="M12 10v4M12 17h.01" />
                </svg>
                <p className="text-[12.5px] leading-relaxed themed-text">
                  <strong>
                    {overAfter === 1
                      ? t('settings.storage.roleDefaultsOverOne')
                      : t('settings.storage.roleDefaultsOverMany', { count: overAfter })}
                  </strong>
                  <br />
                  {t('settings.storage.roleDefaultsOverBody')}
                </p>
              </div>
            ) : (
              <p className="text-[11.5px] themed-text-muted leading-relaxed">{t('settings.storage.roleDefaultsCalm')}</p>
            )}

            <p className="text-[11.5px] themed-text-muted leading-relaxed pt-2 themed-border border-t">
              {t('settings.storage.roleDefaultsAiFootnote')}
            </p>
            {failed && <p className="text-xs text-rose-500">{t('settings.storage.chatStorageSaveFailed')}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2.5 px-6 py-3.5 themed-border border-t">
          <span className="grow" />
          {/* FULL REVIEW 2, FINDING 6 — say WHY Save is off.
              `chatStorageInvalidAmount` was written into all four locales
              and never rendered, so typing `2.5` or `-1` greyed the button
              with no explanation anywhere on screen. */}
          {!allValid ? (
            <span className="text-[12px] text-rose-400 mr-auto self-center">
              {t('settings.storage.chatStorageInvalidAmount')}
            </span>
          ) : changed.length > 0 && writable.length === 0 ? (
            // The third reason Save can be off, added with the skip and
            // without a sentence to go with it: every edit is a clear on a
            // role that carries no override, so there is nothing to remove
            // (round 12, finding 4). Silent-and-grey is better than the
            // previous lit-button-that-writes-nothing, but this file's own
            // rule is to SAY why.
            <span className="text-[12px] themed-text-muted mr-auto self-center">
              {t('settings.storage.chatStorageAlreadyDefault')}
            </span>
          ) : null}
          <button type="button" onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg themed-border border themed-text">
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || writable.length === 0 || !allValid}
            className="text-[13px] px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
          >
            {saving ? t('settings.storage.chatStorageSaving') : t('settings.storage.roleDefaultsSave')}
          </button>
        </div>
      </div>
    </div>
  )
}
